/**
 * Shared shell for Control Portal wizards (Customer + Device registration).
 * Renders the header, stepper, body slot, and footer actions.
 */
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  CheckCircle, ChevronRight, ChevronLeft, Loader2, X,
} from 'lucide-react';

export function WizardShell({
  title,
  steps,
  step,
  loading,
  children,
  onClose,
  onBack,
  onNext,
  onSubmit,
  submitLabel = 'Submit',
  submittingLabel = 'Submitting...',
  backTestId,
  nextTestId,
  submitTestId,
}) {
  const isLast = step >= steps.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{title}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Step {step} of {steps.length}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stepper */}
        <WizardStepper steps={steps} step={step} />

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">{children}</div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
          <Button
            variant="ghost"
            onClick={step === 1 ? onClose : onBack}
            className="text-slate-600 dark:text-slate-400"
            data-testid={backTestId}
          >
            {step === 1 ? 'Cancel' : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </>
            )}
          </Button>

          {!isLast ? (
            <Button
              onClick={onNext}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid={nextTestId}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={onSubmit}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={submitTestId}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {submittingLabel}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {submitLabel}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function WizardStepper({ steps, step }) {
  return (
    <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
      <div className="flex items-center justify-between">
        {steps.map((s, idx) => {
          const Icon = s.icon;
          const isActive = s.id === step;
          const isCompleted = s.id < step;
          return (
            <div key={s.id} className="flex items-center">
              <div className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg transition-all',
                isActive && 'bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-200 dark:ring-blue-800',
                isCompleted && 'text-emerald-600 dark:text-emerald-400',
              )}>
                <div className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center',
                  isActive && 'bg-blue-600 text-white',
                  isCompleted && 'bg-emerald-500 text-white',
                  !isActive && !isCompleted && 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400',
                )}>
                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <div className="hidden sm:block">
                  <p className={cn(
                    'text-sm font-medium',
                    isActive ? 'text-blue-700 dark:text-blue-400' : 'text-slate-600 dark:text-slate-400',
                  )}>{s.title}</p>
                </div>
              </div>
              {idx < steps.length - 1 && (
                <div className={cn(
                  'w-8 h-0.5 mx-1',
                  isCompleted ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-600',
                )} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
